/*
公開の頁 (ログイン不要) の多言語の正本。

住所の決め (2026-10-17 代表、10/26 可視化で最初に実装):
日本語は接頭辞なし (/visualize/...)。他の言語は頭に符号を付ける (/en/visualize/...)。
サイト全体が日本語を既定にしているため、/lab と同じ扱いにする。

ここは「どの言語を持てるか」の器だけを決める。実際にどの頁の組が
どの言語を出しているかは ./page-translations.ts の表 1 か所で持つ。
器にあっても表に無ければ、その頁の住所は 404 になる。
*/

export const DEFAULT_LOCALE = "ja" as const;

export const LOCALE_CODES = [
  "en",
  "es",
  "pt",
  "tr",
  "fr",
  "de",
  "ru",
  "ja",
  "ko",
  "ar",
  "pl",
  "it",
  "he",
] as const;

export type LocaleCode = (typeof LOCALE_CODES)[number];

export interface LocaleMeta {
  code: LocaleCode;
  /* 切り替えの一覧に出す、その言語自身の書き方 (English、日本語)。旗は使わない */
  nativeLabel: string;
  /* <html lang> */
  htmlLang: string;
  /* <html dir>。アラビア語とヘブライ語は右から左 */
  dir: "ltr" | "rtl";
  /* og:locale (Facebook 等が読む形式、アンダースコア区切り) */
  ogLocale: string;
}

export const LOCALES: Record<LocaleCode, LocaleMeta> = {
  ja: { code: "ja", nativeLabel: "日本語", htmlLang: "ja", dir: "ltr", ogLocale: "ja_JP" },
  en: { code: "en", nativeLabel: "English", htmlLang: "en", dir: "ltr", ogLocale: "en_US" },
  es: { code: "es", nativeLabel: "Español", htmlLang: "es", dir: "ltr", ogLocale: "es_ES" },
  pt: { code: "pt", nativeLabel: "Português", htmlLang: "pt", dir: "ltr", ogLocale: "pt_BR" },
  tr: { code: "tr", nativeLabel: "Türkçe", htmlLang: "tr", dir: "ltr", ogLocale: "tr_TR" },
  fr: { code: "fr", nativeLabel: "Français", htmlLang: "fr", dir: "ltr", ogLocale: "fr_FR" },
  de: { code: "de", nativeLabel: "Deutsch", htmlLang: "de", dir: "ltr", ogLocale: "de_DE" },
  ru: { code: "ru", nativeLabel: "Русский", htmlLang: "ru", dir: "ltr", ogLocale: "ru_RU" },
  ko: { code: "ko", nativeLabel: "한국어", htmlLang: "ko", dir: "ltr", ogLocale: "ko_KR" },
  ar: { code: "ar", nativeLabel: "العربية", htmlLang: "ar", dir: "rtl", ogLocale: "ar_AR" },
  pl: { code: "pl", nativeLabel: "Polski", htmlLang: "pl", dir: "ltr", ogLocale: "pl_PL" },
  it: { code: "it", nativeLabel: "Italiano", htmlLang: "it", dir: "ltr", ogLocale: "it_IT" },
  he: { code: "he", nativeLabel: "עברית", htmlLang: "he", dir: "rtl", ogLocale: "he_IL" },
};

export function isLocaleCode(value: string | undefined): value is LocaleCode {
  return !!value && Object.prototype.hasOwnProperty.call(LOCALES, value);
}

/* 日本語の住所 (接頭辞なし) に、言語ごとの接頭辞を付ける。ja はそのまま */
export function localizedPath(jaPath: string, locale: LocaleCode): string {
  if (locale === DEFAULT_LOCALE) return jaPath;
  return `/${locale}${jaPath}`;
}
